import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ScrollText } from 'lucide-react'
import { api } from '../lib/api'
import { Carte, Chargement, EtatVide, Message } from '../composants/base'
import * as f from '../lib/format'

type Evenement = {
  id: string
  action: string
  cible: string | null
  cibleId: string | null
  ip: string | null
  creeLe: string
  patient: { id: string; reference: string; nom: string | null; prenom: string | null } | null
}

// Vocabulaire du journal, traduit pour être lisible.
const ACTIONS: Record<string, string> = {
  'connexion.reussie': 'Connexion',
  'connexion.code.envoye': 'Code de connexion envoyé',
  'connexion.code.refuse': 'Code refusé',
  'connexion.code.inconnu': 'Code demandé pour une adresse sans compte',
  'connexion.code.envoi_echoue': "Échec de l'envoi du code",
  deconnexion: 'Déconnexion',
  'patient.creation': 'Dossier créé',
  'patient.consultation': 'Dossier ouvert',
  'patient.identite.correction': 'Identité corrigée',
  'patient.fusion': 'Dossiers fusionnés',
  'venue.creation': 'Venue créée',
  'venue.correction': 'Venue corrigée',
  'venue.suppression': 'Venue supprimée',
  'document.depot': 'Document déposé',
  'document.texte_colle': 'Texte collé',
  'document.lecture': 'Document ouvert',
  'document.telechargement': 'Document téléchargé',
  'document.correction': 'Document corrigé',
  'document.retrait': 'Document retiré',
  'donnee.saisie_manuelle': 'Donnée saisie',
  'donnee.correction': 'Donnée corrigée',
  'donnee.validation': 'Donnée validée',
  'donnee.rejet': 'Proposition rejetée',
  'donnee.archivage': 'Donnée archivée',
  'conflit.decision': 'Désaccord tranché',
  'profil.correction': 'Profil modifié',
  'profil.signature': 'Signature déposée',
}

export default function Journal() {
  const { data, isLoading } = useQuery({
    queryKey: ['journal'],
    queryFn: () => api<{ evenements: Evenement[] }>('/api/journal'),
  })

  return (
    <div className="mx-auto max-w-[900px] pt-1">
      <h1 className="mb-4 text-[20px] font-semibold tracking-tight">Journal des accès</h1>

      <div className="mb-4">
        <Message ton="info">
          Qui a fait quoi, sur quel dossier, et quand. Aucun contenu médical n’est écrit dans ce
          journal. Les deux cents dernières actions sont affichées.
        </Message>
      </div>

      <Carte icone={<ScrollText className="h-[17px] w-[17px] text-bleu-500" />} titre="Actions">
        {isLoading ? (
          <Chargement />
        ) : !data || data.evenements.length === 0 ? (
          <EtatVide texte="Aucune action enregistrée." />
        ) : (
          <ul className="divide-y divide-trait px-5 pb-3">
            {data.evenements.map((e) => (
              <li key={e.id} className="flex items-center gap-4 py-2.5">
                <span className="w-[150px] shrink-0 text-[12.5px] text-brume">
                  {f.heure(e.creeLe)}
                </span>
                <span className="flex-1 text-[13.5px]">{ACTIONS[e.action] ?? e.action}</span>
                {e.patient ? (
                  <Link
                    to={`/patients/${e.patient.id}`}
                    className="w-[200px] shrink-0 truncate text-right text-[12.5px] text-bleu-600 hover:underline"
                  >
                    {[e.patient.nom?.toUpperCase(), e.patient.prenom].filter(Boolean).join(' ') ||
                      `Dossier ${e.patient.reference}`}
                  </Link>
                ) : (
                  <span className="w-[200px] shrink-0" />
                )}
              </li>
            ))}
          </ul>
        )}
      </Carte>
    </div>
  )
}
